import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { Breadcrumb, SectionHeading } from "@/components/base/Ui";
import { BlacklistRow } from "@/components/base/BlacklistRow";
import { LoadingState, EmptyState } from "@/components/base/StateViews";
import { EventGridCard } from "@/components/base/EventCard";
import { blacklist } from "@/mocks/blacklist";
import { events } from "@/mocks/events";
import type { BlacklistEntry, BlacklistSortKey, EventItem } from "@/types";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const sortOptions: { key: BlacklistSortKey; label: string; icon: string }[] = [
  { key: "score", label: "综合排名", icon: "ri-sort-desc" },
  { key: "form", label: "按状态", icon: "ri-line-chart-line" },
  { key: "defense", label: "按防守", icon: "ri-shield-cross-line" },
  { key: "discipline", label: "按纪律", icon: "ri-alert-line" },
  { key: "efficiency", label: "按效率", icon: "ri-speed-up-line" },
];

export default function BlacklistPage() {
  const [sortKey, setSortKey] = useState<BlacklistSortKey>("score");
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: "今日黑榜mdrs排行 - 51-每日大赛",
    description:
      "51-每日大赛今日黑榜mdrs排行，实时权威展示当日表现最差的队伍与选手，含状态、防守、纪律、效率多指标数据。",
    keywords: "今日黑榜,mdrs排行,黑榜排行,赛事排行,51-每日大赛",
  });

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, [sortKey]);

  const sorted = useMemo(() => {
    const list = [...(blacklist as BlacklistEntry[])];
    if (sortKey === "score") return list.sort((a, b) => b.score - a.score);
    return list.sort((a, b) => b.metrics[sortKey] - a.metrics[sortKey]);
  }, [sortKey]);

  const topScore = Math.max(...(blacklist as BlacklistEntry[]).map((b) => b.score));
  const categoryCount = new Set((blacklist as BlacklistEntry[]).map((b) => b.categoryId)).size;
  const recommended = (events as EventItem[]).slice(0, 3);

  return (
    <Layout>
      <div className="mx-auto w-full px-4 py-8 md:px-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "今日黑榜排行" }]} />

        <header className="mt-5 overflow-hidden rounded-lg border border-accent-200 bg-accent-50/60">
          <div className="p-5 md:p-7">
            <span className="flex w-fit items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
              <i className="ri-fire-line"></i>
              每日更新 · 演示数据
            </span>
            <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
              今日黑榜 mdrs 排行 —— 实时权威展示
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
              依据状态、防守、纪律、效率等多项指标加权计算 mdrs 失分指数，指数越高代表当日表现越差。
              本榜用于帮助用户快速了解负面表现情况，辅助赛事形势分析。
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 border-t border-accent-200 bg-background-50/70 p-5 md:grid-cols-4 md:p-6">
            {[
              { label: "上榜条目", value: `${blacklist.length} 条` },
              { label: "最高失分指数", value: topScore.toFixed(1) },
              { label: "覆盖赛事类别", value: `${categoryCount} 类` },
              { label: "数据更新时间", value: "今日 08:00" },
            ].map((s) => (
              <div key={s.label} className="flex flex-col">
                <span className="text-xl font-extrabold text-accent-700 md:text-2xl">{s.value}</span>
                <span className="mt-1 text-xs text-foreground-500">{s.label}</span>
              </div>
            ))}
          </div>
        </header>

        <section className="mt-8">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-extrabold text-foreground-950 md:text-xl">完整排行列表</h2>
            <div className="flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSortKey(opt.key)}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                    sortKey === opt.key
                      ? "bg-accent-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <i className={`${opt.icon} text-base`}></i>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <LoadingState label="正在加载今日黑榜数据…" />
          ) : sorted.length === 0 ? (
            <EmptyState
              title="今日暂无黑榜数据"
              desc="今日黑榜排行尚未生成，请稍后刷新查看，或先浏览赛事资讯。"
              actionLabel="刷新排行"
              onAction={() => {
                setLoading(true);
                setTimeout(() => setLoading(false), 500);
              }}
              icon="ri-fire-line"
            />
          ) : (
            <div className="flex flex-col gap-3">
              {sorted.map((entry) => (
                <BlacklistRow key={entry.name} entry={entry} />
              ))}
            </div>
          )}
        </section>

        <section className="mt-12 mb-4">
          <SectionHeading
            title="相关赛事推荐"
            subtitle="黑榜条目对应的关联赛事资讯，点击查看详细战况"
            icon="ri-links-line"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {recommended.map((e) => (
              <EventGridCard key={e.id} event={e} />
            ))}
          </div>
          <div className="mt-5 text-center">
            <Link
              to="/category/football"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
            >
              <i className="ri-arrow-left-line"></i>
              返回浏览赛事资讯
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}